import React, { useState, useEffect, useRef } from 'react';
import { searchProducts } from '../services/product/product-service';

const ProductCodeAutocomplete = ({ value, onProductSelect, onCodeChange, className }) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = async (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        onCodeChange(newValue);

        if (newValue.length >= 2) {
            setIsLoading(true);
            setShowDropdown(true);
            try {
                const result = await searchProducts({
                    searchTerm: newValue,
                    page: 0,
                    size: 10
                });
                
                if (result && result.value) {
                    setSuggestions(result.value);
                } else {
                    setSuggestions([]);
                }
            } catch (error) {
                console.error('Error searching products:', error);
                setSuggestions([]);
            } finally {
                setIsLoading(false);
            }
        } else {
            setSuggestions([]);
            setShowDropdown(false);
        }
    };

    const handleSuggestionClick = (product) => {
        setInputValue(product.code);
        setShowDropdown(false);
        setSuggestions([]);
        onProductSelect(product);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => {
                    if (suggestions.length > 0) {
                        setShowDropdown(true);
                    }
                }}
                className={className || "border p-2 rounded w-full"}
                placeholder="Enter product code"
            />
            {showDropdown && (
                <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
                    {isLoading ? (
                        <div className="px-4 py-2 text-gray-500">Loading...</div>
                    ) : suggestions.length > 0 ? (
                        suggestions.map((product) => (
                            <div
                                key={product.id}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                                onClick={() => handleSuggestionClick(product)}
                            >
                                <div className="font-semibold">{product.code}</div>
                                <div className="text-sm text-gray-600">{product.name}</div>
                                <div className="text-xs text-gray-500">
                                    {product.price} EUR | {product.type} | {product.company}
                                </div>
                            </div>
                        ))
                    ) : inputValue.length >= 2 ? (
                        <div className="px-4 py-2 text-gray-500">No products found</div>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default ProductCodeAutocomplete;
